import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { SearchProductsQuery } from '../../../common/generated-types';
import { CompareDialogComponent } from '../../../shared/components/compare-dialog/compare-dialog.component';
import { CompareService } from '../../providers/compare/compare.service';
import { DiscountsService } from '../../providers/discounts/discounts.service';
import { ModalService } from '../../providers/modal/modal.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { WishlistService } from '../../providers/wishlist/wishlist.service';
import { StateService } from '../../providers/state/state.service';

@Component({
    selector: 'vsf-product-card',
    templateUrl: './product-card.component.html',
})
export class ProductCardComponent implements OnInit, OnDestroy {

    @Input() product: SearchProductsQuery['search']['items'][number];
    inWishlist = false;
    inCompare = false;
    signedIn = false;
    discountPercent = 0;
    discountedPrice: number | null = null;
    private sub: Subscription;
    private compareSub: Subscription;
    private discountsSub: Subscription;

    constructor(
        private wishlistService: WishlistService,
        private compareService: CompareService,
        private discountsService: DiscountsService,
        private stateService: StateService,
        private notificationService: NotificationService,
        private translate: TranslateService,
        private router: Router,
        private modalService: ModalService,
    ) {}

    ngOnInit() {
        this.stateService.select(state => state.signedIn).subscribe(signedIn => {
            this.signedIn = signedIn;
        });
        this.sub = this.wishlistService.wishlistIds$.subscribe(ids => {
            const variantId = this.product?.productVariantId || this.product?.productId;
            this.inWishlist = variantId ? ids.has(variantId) : false;
        });
        this.compareSub = this.compareService.compareIds$.subscribe(ids => {
            const productId = this.product?.productId;
            this.inCompare = productId ? ids.includes(productId) : false;
        });
        this.discountsSub = combineLatest([
            this.discountsService.productDiscounts$,
            this.discountsService.variantDiscounts$,
        ]).subscribe(([productMap, variantMap]) => {
            const variantId = this.product?.productVariantId;
            const productId = this.product?.productId;
            const variantPct = variantId ? (variantMap.get(String(variantId)) ?? 0) : 0;
            const productPct = productId ? (productMap.get(String(productId)) ?? 0) : 0;
            const pct = variantPct > 0 ? variantPct : productPct;
            this.discountPercent = pct;
            const min = (this.product as any)?.priceWithTax?.min;
            this.discountedPrice = pct > 0 && typeof min === 'number'
                ? Math.round(min * (1 - pct / 100))
                : null;
        });
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
        this.compareSub?.unsubscribe();
        this.discountsSub?.unsubscribe();
    }

    toggleWishlist(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.signedIn) {
            return;
        }
        const variantId = this.product?.productVariantId || this.product?.productId;
        if (!variantId) {
            return;
        }
        this.wishlistService.toggleWishlist(variantId).subscribe();
    }

    toggleCompare(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        const productId = this.product?.productId;
        if (!productId) {
            return;
        }
        const alreadyHas = this.compareService.has(productId);
        if (!alreadyHas && this.compareService.count >= CompareService.MAX) {
            this.notificationService
                .error(this.translate.instant('compare.limitReached'))
                .subscribe();
            return;
        }
        if (!alreadyHas) {
            this.compareService.toggle(productId);
        }
        this.modalService
            .fromComponent(CompareDialogComponent, {
                locals: {
                    firstProduct: {
                        id: productId,
                        name: this.product.productName,
                        slug: this.product.slug,
                        asset: this.product.productAsset ?? null,
                    },
                },
            })
            .subscribe();
    }
}
